import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { 
  signInWithEmailAndPassword as webSignInWithEmailAndPassword,
  createUserWithEmailAndPassword as webCreateUserWithEmailAndPassword,
  sendPasswordResetEmail as webSendPasswordResetEmail,
  confirmPasswordReset as webConfirmPasswordReset,
  ActionCodeSettings,
  Auth,
  User
} from 'firebase/auth';

// Type for authentication result - works for both native and web
export type AuthResult = {
  user: User | any; // Capacitor returns a different User type, so we use any for compatibility
};

/**
 * Signs in a user with email and password
 * @param email - User's email address
 * @param password - User's password
 * @param auth - Firebase Auth instance (web only)
 * @returns Promise that resolves with the authentication result
 */
export const signInWithEmailAndPassword = async (
  email: string,
  password: string,
  auth?: Auth
): Promise<AuthResult> => {
  // Validate email
  if (!email || !email.includes('@')) {
    throw new Error('Invalid email address');
  }

  // Validate password
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }

  if (Capacitor.isNativePlatform()) {
    // Use Capacitor Firebase Auth for native platforms
    try {
      const result = await FirebaseAuthentication.signInWithEmailAndPassword({
        email,
        password,
      });
      
      if (!result.user) {
        throw new Error('Sign in failed: No user returned from authentication');
      }
      
      return { user: result.user };
    } catch (error: any) {
      throw error.message;
    }
  } else {
    // Use Firebase Web SDK for web
    if (!auth) {
      throw new Error('Auth instance is required for web platform');
    }

    try {
      const userCredential = await webSignInWithEmailAndPassword(auth, email, password);
      return { user: userCredential.user };
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || 'Unknown error';
      throw errorMessage;
    }
  }
};

/**
 * Creates a new user account with email and password
 * @param email - User's email address
 * @param password - User's password
 * @param auth - Firebase Auth instance (web only)
 * @returns Promise that resolves with the authentication result
 */
export const createUserWithEmailAndPassword = async (
  email: string,
  password: string,
  auth?: Auth
): Promise<AuthResult> => {
  // Validate email
  if (!email || !email.includes('@')) {
    throw new Error('Invalid email address');
  }

  // Validate password
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }

  if (Capacitor.isNativePlatform()) {
    // Use Capacitor Firebase Auth for native platforms
    try {
      const result = await FirebaseAuthentication.createUserWithEmailAndPassword({
        email,
        password,
      });
      
      if (!result.user) {
        throw new Error('Account creation failed: No user returned from authentication');
      }
      
      return { user: result.user };
    } catch (error: any) {
      throw error.message;
    }
  } else {
    // Use Firebase Web SDK for web
    if (!auth) {
      throw new Error('Auth instance is required for web platform');
    }

    try {
      const userCredential = await webCreateUserWithEmailAndPassword(auth, email, password);
      return { user: userCredential.user };
    } catch (error: any) {
      throw error.message;
    }
  }
};

/**
 * Sends a password reset email to the user
 * @param email - User's email address
 * @param auth - Firebase Auth instance (web only)
 * @returns Promise that resolves when the email is sent
 */
export const sendPasswordResetEmail = async (
  email: string,
  auth?: Auth
): Promise<void> => {
  // Validate email
  if (!email || !email.includes('@')) {
    throw new Error('Invalid email address');
  }

  if (Capacitor.isNativePlatform()) {
    // Use Capacitor Firebase Auth for native platforms
    try {
      await FirebaseAuthentication.sendPasswordResetEmail({
        email,
        // Note: Capacitor Firebase Auth doesn't support actionCodeSettings
        // To customize the reset URL, configure it in Firebase Console:
        // Firebase Console → Authentication → Templates → Password reset → Edit
        // Change the action URL in the email template to: https://develop.nuroleap.com/reset-password?mode=resetPassword&oobCode=%LINK%
      });
    } catch (error: any) {
      throw error.message;
    }
  } else {
    // Use Firebase Web SDK for web
    if (!auth) {
      throw new Error('Auth instance is required for web platform');
    }

    try {
      // Configure action URL to redirect to your app after password reset
      // Firebase will append ?mode=resetPassword&oobCode=... to this URL
      // 
      // IMPORTANT: For this to work, you MUST:
      // 1. Add your domain to Firebase Console → Authentication → Settings → Authorized domains
      //    (e.g., develop.nuroleap.com, app.nuroleap.com)
      // 2. OR customize the email template in Firebase Console → Authentication → Templates → Password reset
      //    and change the action URL in the email template itself
      //
      // If the domain is not authorized, Firebase will ignore actionCodeSettings and use its default page
      const baseUrl = import.meta.env.VITE_BASE_URL || 
                      (import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace('/core', '') : null) ||
                      'https://develop.nuroleap.com'; // Fallback to your production domain
      const resetUrl = `${baseUrl}/reset-password`;
      
      const actionCodeSettings: ActionCodeSettings = {
        url: resetUrl,
        handleCodeInApp: false, // Redirect to our custom page instead of Firebase's default
      };
      
      await webSendPasswordResetEmail(auth, email, actionCodeSettings);
    } catch (error: any) {
      throw error.message;
    }
  }
};

/**
 * Confirms a password reset using the action code from the email link
 * @param oobCode - The action code from the password reset email link
 * @param newPassword - The new password to set
 * @param auth - Firebase Auth instance (web only)
 * @returns Promise that resolves when the password is reset
 */
export const confirmPasswordReset = async (
  oobCode: string,
  newPassword: string,
  auth?: Auth
): Promise<void> => {
  // Validate password
  if (!newPassword || newPassword.length < 6) {
    throw new Error('Password must be at least 6 characters');
  }

  // Validate oobCode
  if (!oobCode) {
    throw new Error('Invalid reset code');
  }

  if (Capacitor.isNativePlatform()) {
    // Use Capacitor Firebase Auth for native platforms
    try {
      await FirebaseAuthentication.confirmPasswordReset({
        oobCode,
        newPassword,
      });
    } catch (error: any) {
      throw error.message;
    }
  } else {
    // Use Firebase Web SDK for web
    if (!auth) {
      throw new Error('Auth instance is required for web platform');
    }

    try {
      await webConfirmPasswordReset(auth, oobCode, newPassword);
    } catch (error: any) {
      throw error.message;
    }
  }
};

/**
 * Handles the complete authentication flow with email and password
 * @param email - User's email address
 * @param password - User's password
 * @param mode - Authentication mode ('signin' | 'signup')
 * @param auth - Firebase Auth instance (web only)
 * @returns Promise that resolves with the authentication result
 */
export const handleEmailPasswordAuth = async (
  email: string,
  password: string,
  mode: 'signin' | 'signup',
  auth?: Auth
): Promise<AuthResult & { isNewUser?: boolean; mode?: 'signin' | 'signup' }> => {
  // Authenticate with Firebase
  let result: AuthResult;
  if (mode === 'signup') {
    result = await createUserWithEmailAndPassword(email, password, auth);
  } else {
    result = await signInWithEmailAndPassword(email, password, auth);
  }
  
  if (!result || !result.user) {
    throw new Error(`${mode === 'signup' ? 'Account creation' : 'Sign in'} failed: No user returned`);
  }

  // Get Firebase ID token to authenticate with backend
  let idToken: string;
  if (Capacitor.isNativePlatform()) {
    // For native platforms, get ID token from Capacitor Firebase Auth
    const tokenResult = await FirebaseAuthentication.getIdToken();
    idToken = tokenResult.token;
  } else {
    // For web, get ID token from Firebase Web SDK
    if (!auth || !result.user) {
      throw new Error('Auth instance and user are required for web platform');
    }
    // result.user is a Firebase User from firebase/auth, which has getIdToken method
    const firebaseUser = result.user as User;
    idToken = await firebaseUser.getIdToken();
  }

  // Try to call backend to create/login user and get JWT token
  // If backend fails, continue with Firebase authentication (like web does)
  try {
    const { JWTManager } = await import('../../utils/jwt');
    const { ApiConfig } = await import('../../config/api');
    
    const apiUrl = `${ApiConfig.baseUrl}/core/auth/login-firebase`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: idToken }),
    }).catch((fetchError: any) => {
      // Log network errors but don't throw - continue with Firebase auth
      return null;
    });

    if (response && response.ok) {
      const backendResponse = await response.json();
      
      // Backend returns { id, username, email, logeado, token } directly
      if (backendResponse.token && backendResponse.id) {
        // Store JWT token
        JWTManager.setToken(backendResponse.token);
        
        // Store user info
        localStorage.setItem('nuroleap_user_id', String(backendResponse.id));
        
        // Check if user profile exists to determine if it's a new user
        let isNewUser = false;
        if (mode === 'signup') {
          // For signup, check if user profile exists
          try {
            const { AuthApiManager } = await import('../apiManager/AuthApiManager');
            const authApi = AuthApiManager.getInstance();
            const profileResponse = await authApi.checkUserProfile(backendResponse.id);
            // If profile doesn't exist or is empty, it's a new user
            isNewUser = !profileResponse.success || !profileResponse.data;
          } catch (error) {
            isNewUser = true;
          }
        }
        return { 
          user: result.user, 
          isNewUser,
          mode
        };
      }
    } else if (response) {
      // Backend responded but with error status
      const statusText = response.statusText || 'Unknown error';
      const status = response.status;
      
      let errorData: any = {};
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `Backend authentication failed (${status}: ${statusText})` };
      }
    }
  } catch (error: any) {
    console.warn('Unexpected error during authentication');
  }
  
  // Store Firebase user info
  if (result.user?.uid) {
    localStorage.setItem('nuroleap_user_id', result.user.uid);
  }
  
  // For native platforms, we need to trigger auth state update in AuthContext
  // The AuthContext will check for Firebase user on next render
  if (Capacitor.isNativePlatform()) {
    // Import and trigger auth state update
    try {
      // Get the auth context update function
      // We'll trigger it by dispatching a custom event that AuthContext can listen to
      window.dispatchEvent(new CustomEvent('firebase-auth-success', { 
        detail: { 
          user: result.user,
          uid: result.user?.uid,
          email: result.user?.email 
        } 
      }));
    } catch (error) {
      console.warn('Could not dispatch auth success event:', error);
    }
  }
  
  // Determine if it's a new user based on mode
  const isNewUser = mode === 'signup';
  
  return { 
    user: result.user, 
    isNewUser,
    mode
  };
};


